import _ from 'lodash'
import axios from 'axios'
import JWT from 'jsonwebtoken'
import moment from 'moment'
import Helper from '@/src/utils/helper'
import CONSTANTS from '@/src/constants'

const _LOG_TOKEN = token => {
    const decoded = JWT.decode(token)
    console.log('decoded', JSON.stringify(decoded, null, 2))
}

const _TOKEN_CHECK = (accessToken, refreshToken) => {
    const now = moment()
    let decoded = JWT.decode(accessToken)
    let mom = moment(decoded.exp * 1000)
    if (mom.isBefore(now)) return {expired: true} // session already expired

    decoded = JWT.decode(refreshToken)
    mom = moment(decoded.exp * 1000)
    // console.log('refresh token exp in %sm', mom.diff(now, 'm'))
    if (mom.isBefore(now)) return {expired: true} // token already expired
    if (mom.diff(now, 'm') < CONSTANTS.REFRESH_TOKEN__MIN) return {refresh: true}

    return false
}

export const state = {
    currentUser: Helper.getStore('auth.currentUser'),
    refreshToken: null,
    loggedOut: false
}

export const mutations = {
    SET_CURRENT_USER(state, sessionUser) {
        if (sessionUser) {
            sessionUser.settings = Helper.parseJSON(_.get(sessionUser, 'user.userSetting'))
            if (sessionUser.user) sessionUser.user.audit = null // NOTE remove audit list until we need it
            // get current group
            const lastOrg = _.get(sessionUser.settings, 'lastOrganisationId')
            const orgs = _.get(sessionUser, 'user.organizations')
            let currentOrg = _.find(orgs, org => _.get(org, 'organizationId') === lastOrg)
            // if lastOrg === null or not found -> use first org in list
            currentOrg = currentOrg || orgs[0]
            sessionUser.organizationGroup = _.get(currentOrg, 'organizationGroup')
        }
        state.currentUser = sessionUser
        Helper.setStore('auth.currentUser', state.currentUser)
    },
    SET_USER_SESSION(state, session) {
        state.currentUser.session = session
        Helper.setStore('auth.currentUser', state.currentUser)
    },
    // update group NOTE doesn't trigger state.currentUser change
    SET_USER_GROUP(state, organizationGroup) {
        state.currentUser.organizationGroup = organizationGroup
        Helper.setStore('auth.currentUser', state.currentUser)
    },
    SET_USER_ORGANISATIONS(state, organisations) {
        state.currentUser.user.organizations = organisations
        Helper.setStore('auth.currentUser', state.currentUser)
    },
    SET_USER_SETTINGS(state, settings) {
        const userSettings = _.omit(settings, ['userId'])
        state.currentUser.settings = Object.assign({}, state.currentUser.settings, userSettings)
        state.currentUser.user.userSetting = JSON.stringify(state.currentUser.settings)
        Helper.setStore('auth.currentUser', state.currentUser)
    },
    SET_REFRESH_TOKEN(state, token) {
        state.refreshToken = token
    },
    SET_LOGIN(state) {
        state.loggedOut = false
    },
    SET_LOGOUT(state) {
        state.loggedOut = true
    }
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({dispatch}) {
        dispatch('validate', {initial: true})
    },

    // Logs in the current user.
    logIn({commit, dispatch, getters}, {username, password} = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return new Promise((resolve, reject) => {
            // TODO username is send in body, encoding really needed?
            username = encodeURIComponent(username)
            axios.post(Helper.apiUrl(CONSTANTS.ROUTES.USERS_LOGIN), {username, password})
                .then(async response => {
                    const session = response.data
                    await dispatch('api/INIT', null, {root: true})
                    dispatch('setToken', session).then(resolve).catch(reject)
                })
                .catch(err => {
                    commit('SET_CURRENT_USER', null)
                    reject(err)
                })
        })
    },

    // Logs out the current user.
    async logOut({commit, dispatch, state, rootState}, expired = false) {
        const userId = _.get(state.currentUser, 'user.userId')
        const userToken = _.get(state.currentUser, 'session.access_token')

        // set loggedOut property for the user to true
        commit('SET_LOGOUT')

        // reset all api states
        await dispatch('api/RESET', null, {root: true})
        // remove all vgf namespaces
        await dispatch('vgf/GFDELETEALL', null, {root: true})

        // logout current user
        commit('SET_CURRENT_USER', null)
        commit('SET_REFRESH_TOKEN', null)

        // clean up app settings
        commit('app/SET_MESSAGES', [], {root: true})

        if (expired) return // no extra logout needed, token is already invalidated

        const headers = {Authorization: `Bearer ${userToken}`}
        const config = {headers}
        return axios.post(Helper.apiUrl(CONSTANTS.ROUTES.USERS_LOGOUT), {userId}, config)
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    async validate({commit, dispatch, state}, {initial = false} = {}) {
        if (!state.currentUser) return Promise.resolve(null)
        if (state.loggedOut) return Promise.resolve(null) // prevent validation while logging out

        const accessToken = _.get(state.currentUser, 'session.access_token')
        const refreshToken = _.get(state.currentUser, 'session.refresh_token')

        const tokenState = _TOKEN_CHECK(accessToken, refreshToken)
        if (tokenState) {
            if (tokenState.refresh) {
                console.info('REFRESH TOKEN!')
                await dispatch('refreshToken', refreshToken)
                return Promise.resolve(state.currentUser)
            }
            if (tokenState.expired) {
                console.info('TOKEN EXPIRED!')
                await dispatch('logOut', true)
                return Promise.resolve(null)
            }
        }

        // TODO set state only in initial?
        commit('SET_CURRENT_USER', state.currentUser)

        if (initial) {
            commit('api/SET_USER_TOKEN', accessToken, {root: true})
            commit('SET_REFRESH_TOKEN', refreshToken)
            await dispatch('api/USER_ORGS', _.get(state.currentUser, 'user.organizations'), {root: true})
            await dispatch('api/INIT', null, {root: true})
        }

        return Promise.resolve(state.currentUser)
    },

    refreshToken({commit, dispatch, state}, _refreshToken) {
        const refreshToken = _refreshToken || state.refreshToken
        // TODO validate refreshToken value?
        if (!refreshToken) return console.warn('refreshToken: token is required')

        return axios.post(Helper.apiUrl(CONSTANTS.ROUTES.USERS_REFRESH_TOKEN), {refreshToken})
            .then(response => {
                if (response.status === 201) {
                    const session = _.get(response, 'data')
                    console.assert(session, 'refresh token: no session returned!')
                    // if token must refreshed on initial validate (no state)
                    // we must dispatch for full reload
                    if (!_refreshToken || !state.refreshToken) dispatch('setToken', session) // full reload
                    else { // only update tokens and user session
                        commit('api/SET_USER_TOKEN', _.get(session, 'access_token'), {root: true})
                        commit('SET_REFRESH_TOKEN', _.get(session, 'refresh_token'))
                        commit('SET_USER_SESSION', session)
                    }
                }
            })
            // TODO invalid grant -> catch should logout?
            .catch(() => {
                dispatch('logOut', true)
            })
    },

    // set tokens
    // load user
    // update user organizations
    setToken({commit, dispatch, state}, session) {
        return new Promise((resolve, reject) => {
            const _FAILED = () => {
                commit('SET_CURRENT_USER', null)
                reject()
            }
            const accessToken = _.get(session, 'access_token')
            if (!accessToken) return _FAILED()

            const decoded = JWT.decode(accessToken)
            const keycloakId = _.get(decoded, 'sub')
            if (!Helper.isValidId(keycloakId)) return _FAILED()

            commit('api/SET_USER_TOKEN', accessToken, {root: true})
            commit('SET_REFRESH_TOKEN', _.get(session, 'refresh_token'))

            dispatch('api/USERS_GET', {keycloakId}, {root: true}).then(user => {
                if (!user) return _FAILED()

                commit('SET_CURRENT_USER', {session, user})
                dispatch('api/USER_ORGS', user.organizations, {root: true})
                resolve({email: user.email})
            })
        })
    },

    organisations({commit}, orgs) {
        commit('SET_USER_ORGANISATIONS', orgs)
    },

    settings({commit}, settings) {
        commit('SET_USER_SETTINGS', settings)
    }
}

// NOTE currentUser is not visible in state, only available by using auth "authComputed" from state/helpers
export const getters = {
    // Whether the user is currently logged in and loggedOut state is not set
    // NOTE state.loggedOut MUST be set to false directly after a successful login
    loggedIn(state) {
        return !!state.currentUser && !state.loggedOut
    },
    settings(state) {
        return state.currentUser?.settings
        // return _.get(state, 'currentUser.settings')
    },
    developerMode(state) {
        if (location.hostname === 'localhost' && location.port === '9009') return true
        return _.get(JWT.decode(_.get(state.currentUser, 'session.access_token')), 'UserGroup', []).includes('/Developers')
    }
}
